import axios from 'axios';
import { getBuildTimeApi } from '../config/buildTimeConfig';
import { clearAuth, getTenant, getToken, getUser, updateAuthToken } from './auth';

const buildTimeConfig = getBuildTimeApi();
const API_BASE = buildTimeConfig.baseUrl || process.env.REACT_APP_API_BASE_URL || '';
const LOCAL_TENANT_INPUT = buildTimeConfig.tenantId || process.env.REACT_APP_TENANT_ID || '';
const API_CHANNEL_DEBUG = String(process.env.REACT_APP_API_CHANNEL_DEBUG || '').toLowerCase() === 'true';

const normalizeTenantId = (value = '') => {
  const input = String(value || '').trim().toLowerCase();
  if (!input) return '';
  if (input.includes('.')) {
    const [subdomain] = input.split('.').filter(Boolean);
    return subdomain || '';
  }
  return input;
};

const resolveTenantConfigFromEnv = (value = '') => {
  const input = String(value || '').trim().toLowerCase();
  if (!input) return { tenantId: '', tenantHost: '' };
  if (!input.includes('.')) return { tenantId: input, tenantHost: '' };
  return {
    tenantId: normalizeTenantId(input),
    tenantHost: input,
  };
};

const LOCAL_TENANT_CONFIG = resolveTenantConfigFromEnv(LOCAL_TENANT_INPUT);

const AUTH_ENDPOINTS = [
  '/ssp/token/generate-token',
  '/ssp/otp/request',
  '/ssp/otp/verify',
];

const isAuthEndpoint = (url = '') => AUTH_ENDPOINTS.some((endpoint) => String(url).includes(endpoint));

const getRequestPath = (url = '') => {
  const input = String(url || '');
  if (!input) return '';
  if (/^https?:\/\//i.test(input)) {
    try {
      return new URL(input).pathname || '';
    } catch {
      return input;
    }
  }
  return input;
};

const isDebugEnabled = () =>
  API_CHANNEL_DEBUG && typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';

const resolveAuthChannel = (url = '') => {
  const path = getRequestPath(url);
  if (path.startsWith('/ssp/')) return 'SSP_CBS_AUTH';
  if (path.startsWith('/token/')) return 'INTERNAL_DB_AUTH';
  return 'BUSINESS_BEARER_AUTH';
};

const createIdempotencyKey = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const resolveTenantIdFromHost = () => {
  if (typeof window === 'undefined') return LOCAL_TENANT_CONFIG.tenantId;

  const hostname = String(window.location.hostname || '').toLowerCase();
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.localhost') ||
    hostname.endsWith('.local');

  if (isLocalHost) return LOCAL_TENANT_CONFIG.tenantId;

  const parts = hostname.split('.').filter(Boolean);
  if (parts.length >= 3) {
    const subdomain = parts[0] === 'www' && parts[1] ? parts[1] : parts[0];
    return subdomain || '';
  }

  return '';
};

const resolveTenantId = ({ preferHost = false } = {}) => {
  // Always use tenant ID from config for consistency
  // This ensures frontend and backend use same tenant validation
  return LOCAL_TENANT_CONFIG.tenantId;
};

const withTenantId = (payload, tenantIdFromContext = '') => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
  if (payload instanceof FormData) return payload;

  const inferredTenantId = payload.tenantId || payload.tenant || tenantIdFromContext || resolveTenantId();
  if (isDebugEnabled()) {
    console.log('DEBUG: API tenant ID:', inferredTenantId);
  }
  if (!inferredTenantId) return payload;

  return {
    ...payload,
    tenantId: inferredTenantId,
  };
};

const resolveForwardedHost = () => {
  if (typeof window === 'undefined') return '';

  const { loadBuildTimeConfig } = require('../config/buildTimeConfig');
  const tenantConfig = loadBuildTimeConfig();
  return String(
    tenantConfig.domain ||
    LOCAL_TENANT_CONFIG.tenantHost ||
    window.location.host ||
    ''
  ).trim().toLowerCase();
};

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshTokenPromise = null;
const LAST_REFRESHED_AT_KEY = 'auth_validated_at';
const inFlightGetRequests = new Map();

const stableSerialize = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
  }
  if (typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${key}:${stableSerialize(value[key])}`)
      .join(',')}}`;
  }
  return String(value);
};

const buildDedupeKey = (url = '', params = {}) => {
  const path = getRequestPath(url);
  return `${path}::${stableSerialize(params)}`;
};

const dedupeGet = (url, config = {}) => {
  const key = buildDedupeKey(url, config?.params || {});
  const existingRequest = inFlightGetRequests.get(key);
  if (existingRequest) {
    if (isDebugEnabled()) {
      console.info(`[API][DEDUPE] Reusing in-flight GET ${getRequestPath(url) || '/'}`, {
        params: config?.params || {},
      });
    }
    return existingRequest;
  }

  if (isDebugEnabled()) {
    console.info(`[API][DEDUPE] New GET ${getRequestPath(url) || '/'}`, {
      params: config?.params || {},
    });
  }

  const requestPromise = api
    .get(url, config)
    .finally(() => {
      if (inFlightGetRequests.get(key) === requestPromise) {
        inFlightGetRequests.delete(key);
      }
    });

  inFlightGetRequests.set(key, requestPromise);
  return requestPromise;
};

const normalizeErrorText = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const resolveErrorText = (errorLike = {}) =>
  errorLike?.response?.data?.errorDescription ||
  errorLike?.response?.data?.message ||
  errorLike?.message ||
  '';

const isSessionExpiredError = (errorLike = {}) => {
  const status = Number(errorLike?.response?.status || 0);
  const normalizedText = normalizeErrorText(resolveErrorText(errorLike));

  if (status === 401) return true;

  if (status === 403) {
    if (
      !normalizedText ||
      normalizedText.includes('access denied') ||
      normalizedText.includes('forbidden') ||
      normalizedText.includes('full authentication is required')
    ) {
      return true;
    }
  }

  return (
    normalizedText.includes('jwt expired') ||
    normalizedText.includes('token expired') ||
    normalizedText.includes('expired jwt') ||
    normalizedText.includes('invalid token') ||
    normalizedText.includes('token is invalid') ||
    normalizedText.includes('signature verification failed') ||
    normalizedText.includes('authentication failed') ||
    normalizedText.includes('unauthorized') ||
    normalizedText.includes('full authentication is required')
  );
};

const redirectToLoginIfSessionExpired = (errorLike = {}) => {
  if (!isSessionExpiredError(errorLike)) return false;

  clearAuth();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
  return true;
};

const buildRefreshHeaders = () => {
  const headers = {};
  const token = getToken();
  const forwardedHost = resolveForwardedHost();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (forwardedHost) {
    headers['X-Forwarded-Host'] = forwardedHost;
  }
  return headers;
};

const markRefreshedAt = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(LAST_REFRESHED_AT_KEY, String(Date.now()));
  } catch {
    // Ignore storage errors.
  }
};

const refreshAccessToken = async () => {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  const token = getToken();
  if (!token) return false;

  refreshTokenPromise = refreshClient
    .post('/ssp/token/refresh-token', null, {
      headers: buildRefreshHeaders(),
    })
    .then((response) => {
      const nextToken = response?.data?.token || response?.data?.access_token || '';
      const nextTenant = response?.data?.tenant || getTenant() || '';
      if (!nextToken) {
        throw new Error('Refresh token response is incomplete.');
      }
      updateAuthToken(nextToken, nextTenant);
      markRefreshedAt();
      return true;
    })
    .catch((error) => {
      redirectToLoginIfSessionExpired(error);
      return false;
    })
    .finally(() => {
      refreshTokenPromise = null;
    });

  return refreshTokenPromise;
};

api.interceptors.request.use(
  (config) => {
    const authRequest = isAuthEndpoint(config.url);
    const requestPath = getRequestPath(config.url);
    const tenantId = resolveTenantId({ preferHost: authRequest });
    const headers = config.headers || {};

    const forwardedHost = resolveForwardedHost();
    if (forwardedHost) {
      headers['X-Forwarded-Host'] = forwardedHost;
    } else {
      delete headers['X-Forwarded-Host'];
    }

    const method = String(config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch'].includes(method)) {
      config.data = withTenantId(config.data, tenantId);
    }

    const token = getToken();
    if (token && !authRequest) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      delete headers.Authorization;
    }

    if (isDebugEnabled()) {
      const method = String(config.method || 'GET').toUpperCase();
      const channel = resolveAuthChannel(requestPath);
      // Channel-level trace without leaking secrets.
      console.info(`[API][${channel}] ${method} ${requestPath || '/'}`, {
        hasBearer: Boolean(headers.Authorization),
        tenantId: tenantId || null,
        forwardedHost: forwardedHost || null,
      });
    }

    config.headers = headers;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  async (response) => {
    if (response.data && response.data.success === false) {
      const error = new Error(response.data.errorDescription || 'Request failed');
      error.response = response;
      if (isSessionExpiredError(error) && !response.config?._retry && await refreshAccessToken()) {
        const retriedConfig = {
          ...response.config,
          headers: {
            ...(response.config?.headers || {}),
            Authorization: `Bearer ${getToken()}`,
          },
          _retry: true,
        };
        return api.request(retriedConfig);
      }
      redirectToLoginIfSessionExpired(error);
      throw error;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error?.config || {};
    if (
      isSessionExpiredError(error) &&
      !originalRequest._retry &&
      !String(getRequestPath(originalRequest.url || '')).startsWith('/ssp/token/refresh-token')
    ) {
      originalRequest._retry = true;
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${getToken()}`,
        };
        return api.request(originalRequest);
      }
    }
    redirectToLoginIfSessionExpired(error);
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => {
    return api.post('/ssp/token/generate-token', credentials);
  },

  refreshToken: () => refreshAccessToken(),
  
  requestOTP: (mobileNumber) => {
    return api.post('/ssp/otp/request', { mobileNumber });
  },
  
  verifyOTP: (data) => {
    return api.post('/ssp/otp/verify', data);
  },
  
  logout: async () => {
    try {
      await api.post('/ssp/logout');
    } catch (_error) {
      // Logout must still complete locally even if server call fails.
    } finally {
      clearAuth();
    }
  },
};

export const accountsAPI = {
  getDashboard: () => {
    const user = getUser() || {};
    const customerId = user.customerId || '';
    return dedupeGet(`/member/mobileapp-dashboard/${customerId}`);
  },

  getMemberProfile: (memberGl) => {
    return api.get(`/member/profile/${encodeURIComponent(String(memberGl || '').trim())}`);
  },

  getSuretyTakenFrom: (memberGl) => {
    return dedupeGet(`/member/surety-taken-from/${encodeURIComponent(String(memberGl || '').trim())}`);
  },

  getSuretyGivenTo: (memberGl) => {
    return dedupeGet(`/member/surety-given-to/${encodeURIComponent(String(memberGl || '').trim())}`);
  },
  
  getAccountDetails: (acno, acType) => {
    return api.get(`/account/${acType}/${acno}`);
  },
  
  getStatement: (data) => {
    return api.post('/ledger/statement', data);
  },
};

export const servicesAPI = {
  createRequest: (data) => {
    return api.post('/sr', data);
  },
};

export const feedbackAPI = {
  submitFeedback: (data) => {
    return api.post('/feedback', data);
  },
};

export const offersAPI = {
  getOffers: (params = {}) => {
    return dedupeGet('/member/offers', { params });
  },

  getOfferDetails: (offerId, params = {}) => {
    return dedupeGet(`/member/offers/${encodeURIComponent(String(offerId || '').trim())}`, { params });
  },

  submitInterest: (offerId, data) => {
    return api.post(`/member/offers/${encodeURIComponent(String(offerId || '').trim())}/interest`, data);
  },

  getMyOfferInterests: (params = {}) => {
    return dedupeGet('/member/offers/interests', { params });
  },
};

export const notificationsAPI = {
  getNotifications: (params = {}) => {
    return dedupeGet('/member/notifications', { params });
  },

  updateReadState: (notificationId, data) => {
    return api.patch(`/member/notifications/${encodeURIComponent(String(notificationId || '').trim())}/read-state`, data);
  },

  updateReadStateBulk: (data) => {
    return api.patch('/member/notifications/read-state', data);
  },
};

export const savingsAPI = {
  createAccount: (data, idempotencyKey) => {
    return api.post('/savings/accounts', data, {
      headers: { 'Idempotency-Key': idempotencyKey || createIdempotencyKey() },
    });
  },

  deposit: (accountId, data, idempotencyKey) => {
    return api.post(`/savings/accounts/${encodeURIComponent(accountId)}/deposit`, data, {
      headers: { 'Idempotency-Key': idempotencyKey || createIdempotencyKey() },
    });
  },

  withdraw: (accountId, data, idempotencyKey) => {
    return api.post(`/savings/accounts/${encodeURIComponent(accountId)}/withdraw`, data, {
      headers: { 'Idempotency-Key': idempotencyKey || createIdempotencyKey() },
    });
  },

  getBalance: (accountId) => {
    return api.get(`/savings/accounts/${encodeURIComponent(accountId)}/balance`);
  },

  getTransactionStatus: (accountId, transactionRef) => {
    return api.get(
      `/savings/accounts/${encodeURIComponent(accountId)}/transactions/${encodeURIComponent(transactionRef)}/status`
    );
  },
};

export default api;
